import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Input,
  message,
  Modal,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import {
  BellOutlined,
  CodeOutlined,
  FileSearchOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { useVehicleCompanyData } from '../../../../../../../Hooks/TT_ELD/Vehicles';
import { camera } from '../../../../../../../API/TT_ELD/camera';

const { Text, Title } = Typography;

type TruckCameraProps = {
  vehicleId: string | number | undefined;
};

type CameraInfo = {
  id?: string | number;
  imei?: string | number;
  updatedAt?: string;
  lastUpdated?: string;
  lastSynced?: string;
  lastSync?: string;
  live?: boolean;
  isLive?: boolean;
  online?: boolean;
  isOnline?: boolean;
  vehicleId?: string | number;
  vehicle?: {
    id?: string | number;
    truck_number?: string | number;
  };
  driver?: {
    first_name?: string;
    second_name?: string;
  };
  [key: string]: any;
};

const READY_COMMANDS = [
  {
    label: 'Add wakeup',
    command: 'add_wakeup',
    commandType: 'add_wakeup',
    icon: <BellOutlined rev={undefined} />,
  },
  {
    label: 'Send Logs - all logs to Camera Admin',
    command: 'send_logs',
    commandType: 'send_logs',
    icon: <FileSearchOutlined rev={undefined} />,
  },
  {
    label: 'Status',
    command: 'status',
    commandType: 'status',
    icon: <CodeOutlined rev={undefined} />,
  },
  {
    label: 'Set Voltage',
    command: 'set_voltage',
    commandType: 'set_voltage',
    icon: <ThunderboltOutlined rev={undefined} />,
  },
  {
    label: 'Get Voltage',
    command: 'get_voltage',
    commandType: 'get_voltage',
    icon: <ThunderboltOutlined rev={undefined} />,
  },
];

const renderValue = (value?: React.ReactNode) => {
  if (value === undefined || value === null || value === '') return '-';
  return value;
};

const buildFullName = (person?: { first_name?: string; second_name?: string }) => {
  const fullName = [person?.first_name, person?.second_name].filter(Boolean).join(' ');
  return fullName || '-';
};

const findEmbeddedCamera = (vehicleData: any): CameraInfo | undefined => {
  return (
    vehicleData?.dvr ||
    vehicleData?.camera ||
    vehicleData?.dashcam ||
    vehicleData?.dashCam ||
    vehicleData?.cameraInfo ||
    vehicleData?.dvrInfo
  );
};

const getDvrId = (vehicleData: any, cameraInfo?: CameraInfo) => {
  return (
    cameraInfo?.dvrId ||
    cameraInfo?.id ||
    cameraInfo?.dvr?.id ||
    vehicleData?.dvrId ||
    vehicleData?.dvr?.id ||
    vehicleData?.cameraId ||
    vehicleData?.camera?.id ||
    vehicleData?.dashcam?.id ||
    vehicleData?.dashCam?.id
  );
};

const TruckCamera: React.FC<TruckCameraProps> = ({ vehicleId }) => {
  const { data: vehicleData, isLoading, isFetching } = useVehicleCompanyData(vehicleId);
  const [companyCameras, setCompanyCameras] = useState<CameraInfo[]>([]);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [commandLoading, setCommandLoading] = useState<string>('');
  const [customModalOpen, setCustomModalOpen] = useState(false);
  const [customCommand, setCustomCommand] = useState('');

  useEffect(() => {
    const embeddedCamera = findEmbeddedCamera(vehicleData);
    const companyId = vehicleData?.companyId || vehicleData?.company?.id;

    if (!vehicleData || embeddedCamera || !companyId) return;

    let isMounted = true;
    setCameraLoading(true);

    camera
      .read(true, companyId)
      .then((res) => {
        if (!isMounted) return;
        setCompanyCameras(res?.data || []);
      })
      .finally(() => {
        if (isMounted) setCameraLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [vehicleData]);

  const cameraInfo = useMemo(() => {
    const embeddedCamera = findEmbeddedCamera(vehicleData);
    if (embeddedCamera) return embeddedCamera;

    const currentVehicleId = String(vehicleData?.id || vehicleId || '');
    return companyCameras.find((item) => {
      const cameraVehicleId = item?.vehicleId || item?.vehicle?.id;
      return cameraVehicleId && String(cameraVehicleId) === currentVehicleId;
    });
  }, [companyCameras, vehicleData, vehicleId]);

  const liveValue =
    cameraInfo?.live ?? cameraInfo?.isLive ?? cameraInfo?.online ?? cameraInfo?.isOnline ?? null;

  const sendCommand = async (command: string, commandType = command) => {
    const dvrId = getDvrId(vehicleData, cameraInfo);

    if (!dvrId) {
      message.error('DVR ID not found for this truck camera');
      return;
    }

    setCommandLoading(command);
    try {
      await camera.sendCommandToCamera({
        command,
        commandType,
        dvrId,
        vehicleId: vehicleData?.id || vehicleId,
        companyId: vehicleData?.companyId || vehicleData?.company?.id,
        imei: cameraInfo?.imei,
      });
    } finally {
      setCommandLoading('');
    }
  };

  const handleCustomCommand = async () => {
    const command = customCommand.trim();
    if (!command) return;

    await sendCommand(command);
    setCustomModalOpen(false);
    setCustomCommand('');
  };

  return (
    <Spin spinning={isLoading || isFetching || cameraLoading}>
      <Card style={{ marginTop: 20 }}>
        <Space direction="vertical" size={20} style={{ width: '100%' }}>
          <div>
            <Title level={4} style={{ margin: 0 }}>
              Truck Camera
            </Title>
            <Text type="secondary">Send dashcam commands with one click.</Text>
          </div>

          <Descriptions bordered column={{ xs: 1, sm: 1, md: 2, lg: 3 }}>
            <Descriptions.Item label="IMEI">{renderValue(cameraInfo?.imei)}</Descriptions.Item>
            <Descriptions.Item label="Last Updated">
              {renderValue(cameraInfo?.lastUpdated || cameraInfo?.updatedAt)}
            </Descriptions.Item>
            <Descriptions.Item label="Current Driver">
              {buildFullName(cameraInfo?.driver || vehicleData?.driver)}
            </Descriptions.Item>
            <Descriptions.Item label="Current Truck Number">
              {renderValue(vehicleData?.truck_number || cameraInfo?.vehicle?.truck_number)}
            </Descriptions.Item>
            <Descriptions.Item label="Last Synced">
              {renderValue(cameraInfo?.lastSynced || cameraInfo?.lastSync)}
            </Descriptions.Item>
            <Descriptions.Item label="Live">
              {liveValue === null ? (
                '-'
              ) : (
                <Tag color={liveValue ? 'green' : 'red'}>{liveValue ? 'Live' : 'Offline'}</Tag>
              )}
            </Descriptions.Item>
          </Descriptions>

          {!cameraInfo && (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No assigned camera data found for this truck"
            />
          )}

          <div>
            <Title level={5}>Ready commands</Title>
            <Row gutter={[12, 12]}>
              {READY_COMMANDS.map((item) => (
                <Col xs={24} sm={12} md={8} lg={6} xl={5} key={item.command}>
                  <Button
                    block
                    type="primary"
                    icon={item.icon}
                    loading={commandLoading === item.command}
                    disabled={Boolean(commandLoading)}
                    onClick={() => sendCommand(item.command, item.commandType)}
                  >
                    {item.label}
                  </Button>
                </Col>
              ))}
              <Col xs={24} sm={12} md={8} lg={6} xl={5}>
                <Button
                  block
                  icon={<CodeOutlined rev={undefined} />}
                  disabled={Boolean(commandLoading)}
                  onClick={() => setCustomModalOpen(true)}
                >
                  Custom command
                </Button>
              </Col>
            </Row>
          </div>
        </Space>
      </Card>

      <Modal
        title="Send custom camera command"
        open={customModalOpen}
        onCancel={() => {
          setCustomModalOpen(false);
          setCustomCommand('');
        }}
        onOk={handleCustomCommand}
        confirmLoading={Boolean(commandLoading)}
        okButtonProps={{ disabled: !customCommand.trim() }}
        okText="Send"
      >
        <Input.TextArea
          autoSize={{ minRows: 3, maxRows: 6 }}
          placeholder="Enter command"
          value={customCommand}
          onChange={(event) => setCustomCommand(event.target.value)}
        />
      </Modal>
    </Spin>
  );
};

export default TruckCamera;
